import { Keys, LogoutReason } from '@eih-netbroker/common';
import { AuthProvider as OIDCReactAuthProvider } from 'oidc-react';
import { useCallback } from 'react';
import { Config } from '../shared/config/config';
import { useAuthorizationFlow } from '../shared/services/authorization/useAuthorizationFlow';
import { useLogout } from '../shared/services/authorization/useLogout';
import { MockedMode } from '../shared/utils/useLocalStorage';
import { AuthProvider } from './AuthContext';

export type SSOProviderProps = { children: React.ReactNode };

const isSSOMock =
  !Config.App.isProduction &&
  (
    (localStorage.getItem(Keys.MockedMode)
      ? JSON.parse(localStorage.getItem(Keys.MockedMode) as string)
      : {}) as MockedMode
  ).SSO;

if (isSSOMock) {
  Config.SSO.clientId = 'QzpVA7jHdJVePzBT6oMSkxjg3pJ5YD0m';
  Config.SSO.clientSecret = 'og9mhpfmg1Xg48jxPp9FtBcY18S-y7Yyd5tiD3vsaKbzXF-6Mr8QmjxCU8r_uFoT';
  Config.SSO.discoveryUrl = 'https://dev-f4ym3efpeih5k3hj.us.auth0.com/';
}

export const SSOProvider = ({ children }: SSOProviderProps) => {
  const { loginInternal } = useAuthorizationFlow();
  const { logout } = useLogout();

  /**
   * SSO sign out handler, no need to do anything here
   */
  const onSignOut = useCallback(() => {}, []);

  /**
   * This function handles the case, when user logs in via SSO, and the browser is redirected to Netbroker.
   * In that case AuthContext is not receiving the user data, so we have to handle it here and continue with the login process.
   * @param userData the user data recieved from OIDC client (any is used, because the type is not exported from oidc-react lib)
   */
  const onSSOSignIn = useCallback(
    (userData: (any & { access_token: string; id_token: string }) | null) => {
      if (userData) {
        loginInternal(userData.access_token, userData.id_token);
      } else {
        throw new Error('Error! SSO login failed, userData is null!');
      }
    },
    [loginInternal],
  );

  const onSSOSignInError = useCallback(
    (error: Error) => {
      console.error('SSO sign in error', error);
      // if we are in a popup window or in an iframe, we need to send a message to the parent window to close the popup
      if (window.opener || window.parent) {
        logout(LogoutReason.SSOCallbackError);
      } else {
        logout(LogoutReason.SignInErrorUnknown);
      }
    },
    [logout],
  );

  return (
    <OIDCReactAuthProvider
      onSignIn={onSSOSignIn}
      onSignOut={onSignOut}
      authority={Config.SSO.discoveryUrl}
      clientId={Config.SSO.clientId}
      clientSecret={Config.SSO.clientSecret}
      popupRedirectUri={Config.SSO.redirectUri}
      silentRedirectUri={Config.SSO.redirectUri}
      scope={Keys.SSO.scopes.join(' ')}
      autoSignIn={false}
      autoSignOut={false}
      loadUserInfo={false}
      automaticSilentRenew={false}
      onSignInError={onSSOSignInError}
      extraQueryParams={{}}
    >
      <AuthProvider>{children}</AuthProvider>
    </OIDCReactAuthProvider>
  );
};
